import { DropdownOption } from 'components/final-form/dropdownUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import styled from 'styled-components';
import { Button, Grid } from 'semantic-ui-react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import axios, { AxiosError, CancelTokenSource } from 'axios';
import { noop } from 'utils/functionUtils';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import CollectiveBillInfoSegment from 'components/billing/collective-bill/segments/CollectiveBillInfoSegment';
import CollectiveBillTreatmentsTable from 'components/billing/collective-bill/segments/CollectiveBillTreatmentsTable';
import arrayMutators from 'final-form-arrays';
import { getAllGuarantorsForCollectiveBillTrue } from 'services/guarantorServices';
import { getAllMandateBanksByMandateId } from 'services/mandateBankServices';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import {
  CollectiveBillCreationRequest,
  CollectiveBillDataDto,
  CollectiveBillProofListRequest,
  CollectiveBillResponse,
  GuarantorDto,
  MandateBankDto
} from 'ts-types/api.types';
import CollectiveBillAdditionalInfo from 'components/billing/collective-bill/segments/CollectiveBillAdditionalInfo';
import Select from 'components/final-form/Select';
import PxGrid from 'components/layout/PxGrid';
import EmptyDataMessage from 'components/layout/EmptyDataMessage';
import createDecorator from 'final-form-calculate';
import {
  collectiveBill,
  collectiveBillProofList,
  getCollectiveBillDataForGuarantor,
  getCollectiveBillPdf,
  getCollectiveBillProofListPdf
} from 'services/collectiveBillServices';
import { FormApi } from 'final-form';
import ErrorMessage from 'components/final-form/ErrorMessage';
import PdfModal from 'components/pdf-modal/PdfModal';

const StyledForm = styled.form`
  height: 100%;
`;

const StyledFormContainer = styled(FormContainer)`
  height: 100%;
`;

const FormBottomContent = styled(FormContent)`
  flex: 0 0 auto;
`;

const StyledGuarantorContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 15px;

  .field {
    flex: 1 1 auto;
    max-width: 45%;
  }
`;

const StyledLabel = styled.label`
  margin-right: 10px;
`;

const StyledErrorMessage = styled.div`
  padding-bottom: 1rem;
`;

export interface CollectiveBillPriceInfo {
  amount: number,
  discount: number,
  ahv: number,
  alv: number,
  total: number
}

export interface PatientInfo {
  name: string;
  firstName: string;
  city: string;
  birthDate: string;
}

export interface ExtendedCollectiveBillDataDto extends CollectiveBillDataDto {
  used: boolean
}

interface FinalFormData {
  guarantorId: number,
  bankId: number,
  date: string,
  treatments: Array<ExtendedCollectiveBillDataDto>,
  collectiveBillPriceInfo: CollectiveBillPriceInfo,
  selected?: ExtendedCollectiveBillDataDto,
  billDataId: string,
  errorMessage?: string | Array<string>,
  detailProofList: boolean,
  proofList?: boolean
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
}

interface State {
  guarantors: Array<DropdownOption>,
  mandateBanks: Array<DropdownOption>,
  collectiveBillResponse?: CollectiveBillResponse,
  modalOpen: boolean,
  formData: Partial<FinalFormData>,
  cancelTokenSource: CancelTokenSource
}

class CollectiveBillView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {currentUser} = this.props;

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      guarantors: [],
      mandateBanks: [],
      modalOpen: false,
      formData: {},
      cancelTokenSource: cancelTokenSource
    };

    if (currentUser) {

      const {mandateId} = currentUser.userConfig;

      const methods: Array<Promise<any>> = [
        getAllMandateBanksByMandateId(mandateId, cancelTokenSource),
        getAllGuarantorsForCollectiveBillTrue(cancelTokenSource)
      ];

      Promise.all(methods)
          .then(responses => {

            const mandateBanks: Array<MandateBankDto> = responses[0];
            const mappedBanks: Array<DropdownOption> = mandateBanks.map(mandateBank => ({
              key: mandateBank.id,
              text: `${mandateBank.description}, ${mandateBank.city}, (${mandateBank.bankAccount})`,
              value: mandateBank.id
            }));

            const guarantors: Array<GuarantorDto> = responses[1];
            const mappedGuarantors: Array<DropdownOption> = guarantors.map(guarantor => ({
              key: guarantor.id,
              text: `${guarantor.description}, ${guarantor.zip}, ${guarantor.city}`,
              value: guarantor.id
            }));

            const initialBankId = mappedBanks.length > 0
                ? mappedBanks[0].value
                : undefined;

            this.setState({
              mandateBanks: mappedBanks,
              guarantors: mappedGuarantors,
              formData: this.initialFormData(initialBankId)
            });
          })
          .catch(noop);
    }
  }

  componentWillUnmount(): void {

    const {cancelTokenSource} = this.state;
    cancelTokenSource.cancel('Operations canceled by user');
  }

  initialFormData = (initialBankId?: number): Partial<FinalFormData> => {

    const {options: {workDate}} = this.props;
    const {mandateBanks} = this.state;

    return {
      date: workDate,
      bankId: initialBankId
          ? initialBankId
          : mandateBanks.length === 1
              ? mandateBanks[0].value
              : undefined,
      collectiveBillPriceInfo: {
        amount: 0,
        discount: 0,
        ahv: 0,
        alv: 0,
        total: 0
      }
    }
  }

  openCollectiveBillPdfModal = (collectiveBillResponse: CollectiveBillResponse) => {
    this.setState({
      modalOpen: true,
      collectiveBillResponse: collectiveBillResponse
    })
  }

  closeCollectiveBillPdfModal = () => {
    this.setState({
      modalOpen: false,
      collectiveBillResponse: undefined
    })
  }

  updateFormData = (formData?: Partial<FinalFormData>) => {
    if (!formData) {
      this.setState({
        formData: this.initialFormData()
      });
    } else {
      this.setState({
        formData: formData
      })
    }
  }

  updateSelectedTreatment = (form: FormApi) => (collectiveBillDataDto: ExtendedCollectiveBillDataDto) => {
    form.change("selected", collectiveBillDataDto)
  }

  guarantorDecorator = createDecorator({
    field: "guarantorId",
    updates: (guarantorId: number): Partial<FinalFormData> | Promise<Partial<FinalFormData>> => {

      const {cancelTokenSource} = this.state;
      const {mandateId} = this.props.currentUser!.userConfig;

      if (guarantorId) {
        return getCollectiveBillDataForGuarantor(guarantorId, cancelTokenSource)
            .then(response => {

              const mappedCollectiveBillData: Array<ExtendedCollectiveBillDataDto> = response.collectiveBillData
                  .map(collectiveBillData => ({
                    ...collectiveBillData,
                    used: collectiveBillData.mandateId === mandateId
                  }));

              return {
                treatments: mappedCollectiveBillData,
                collectiveBillPriceInfo: {
                  amount: response.totalPrice,
                  discount: 0,
                  ahv: 0,
                  alv: 0,
                  total: 0
                },
                billDataId: response.billDataId
              }
            });
      }

      return {
        treatments: [],
        collectiveBillPriceInfo: {
          amount: 0,
          discount: 0,
          ahv: 0,
          alv: 0,
          total: 0
        },
        billDataId: undefined
      }
    }
  })

  amountCalculationDecorator = createDecorator({
    field: /^treatments\[.*]\.used$/,
    updates: {
      collectiveBillPriceInfo: (used: boolean, allValues?: Partial<FinalFormData>) => {

        if (allValues) {
          const {treatments = []} = allValues;

          const amount = treatments
              .filter(treatment => treatment.used)
              .map(treatment => treatment.price)
              .reduce((tPrice1, tPrice2) => tPrice1 + tPrice2, 0);

          return {
            amount: amount,
            discount: 0,
            ahv: 0,
            alv: 0,
            total: 0
          }
        }
      }
    }
  });

  handleSubmit = async (values: Partial<FinalFormData>) => {

    const {cancelTokenSource} = this.state;
    const {currentUser, options: {workDate}, getMessage} = this.props;
    const {treatments = [], bankId, date = workDate, billDataId, guarantorId, proofList, detailProofList} = values;

    if (currentUser && guarantorId) {
      const {mandateId} = currentUser.userConfig;

      let errorMessage: string | Array<string> | undefined = undefined;

      if (!proofList) {
        const excludedTreatmentIds: Array<number> = treatments
            .filter(treatment => !treatment.used)
            .map(treatment => treatment.treatmentId);

        if (excludedTreatmentIds.length != treatments.length) {
          const creationRequest: Partial<CollectiveBillCreationRequest> = {
            billDataId: billDataId,
            bankId: bankId,
            mandateId: mandateId,
            billDate: date,
            excludedTreatmentIds: excludedTreatmentIds
          }

          await collectiveBill(guarantorId, creationRequest, cancelTokenSource)
              .then(response => {
                this.updateFormData();
                if (response) {
                  this.openCollectiveBillPdfModal(response);
                }
              })
              .catch((error: AxiosError) => {
                errorMessage = this.setErrorMessage(error);
              });
        } else {
          errorMessage = getMessage("error.noTreatmentsForCollectiveBill");
        }
      } else {
        const treatmentIds: Array<number> = treatments
            .filter(treatment => treatment.used)
            .map(treatment => treatment.treatmentId);

        if (treatmentIds.length > 0) {

          const proofListRequest: Partial<CollectiveBillProofListRequest> = {
            bankId: bankId,
            mandateId: mandateId,
            billDate: date,
            treatmentIds: treatmentIds,
            detail: detailProofList
          }

          await collectiveBillProofList(guarantorId, proofListRequest, cancelTokenSource)
              .then(response => {
                this.updateFormData(values);
                this.openCollectiveBillPdfModal(response);
              })
              .catch((error: AxiosError) => {
                errorMessage = this.setErrorMessage(error);
              });

        } else {
          errorMessage = getMessage("error.noTreatmentsForCollectiveBillProofList");
        }
      }

      if (errorMessage) {
        return {
          errorMessage: errorMessage
        }
      }
    }
  }

  setErrorMessage = (error: AxiosError): string | Array<string> => {
    const {response} = error;
    const {getMessage} = this.props;

    const errorMessages: Set<string> = new Set<string>();
    if (response && response.data && response.data.violations) {

      const violations: Array<{ errorCode: string }> = response.data.violations;

      if (violations.length > 0) {
        for (let i = 0; i < violations.length; i++) {
          const violation = violations[i];
          errorMessages.add(getMessage(`errorCode.${violation.errorCode}`));
        }

        let errorMessage: Array<string> = [];
        errorMessages.forEach(error => (errorMessage as Array<string>).push(error));
        return errorMessage;
      }
    }

    return getMessage("error.general");
  };

  render(): React.ReactElement {

    const {modalOpen, collectiveBillResponse, formData} = this.state;
    const collectiveBillResponses = [collectiveBillResponse];

    return (
        <PraxStyledSegment>
          <FinalForm
              onSubmit={this.handleSubmit}
              initialValues={formData}
              mutators={{
                ...arrayMutators
              }}
              decorators={[this.guarantorDecorator, this.amountCalculationDecorator]}
              subscription={{values: true, pristine: true, submitting: true}}
              render={this.renderForm}
          />
          {modalOpen &&
          <PdfModal
              pdfResponses={collectiveBillResponses}
              modalOpen={modalOpen}
              onCloseModal={this.closeCollectiveBillPdfModal}
              getPdfUrl={this.getCollectiveBillPdfUrl}
          />
          }
        </PraxStyledSegment>
    );
  }

  getCollectiveBillPdfUrl = (response: CollectiveBillResponse) => {
    return response.proofList
        ? getCollectiveBillProofListPdf(response.pdfId, response.pdfName)
        : getCollectiveBillPdf(response.pdfId, response.pdfName);
  };

  renderForm = ({handleSubmit, submitting, values, form}: FormRenderProps): React.ReactNode => {

    const {getMessage} = this.props;
    const {guarantors, mandateBanks} = this.state;

    const disabledButtons = submitting
        || !values.guarantorId
        || !values.treatments
        || values.treatments.length == 0
        || !values.treatments.filter((t: ExtendedCollectiveBillDataDto) => t.used).length

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledFormContainer>
            <FormContent>
              <StyledErrorMessage>
                <Field
                    name="errorMessage"
                    component={ErrorMessage}
                    onDismiss={() => {form.reset(values)}}
                />
              </StyledErrorMessage>
              <StyledGuarantorContainer>
                <StyledLabel>{getMessage('guarantor')}:</StyledLabel>
                <Field
                    name="guarantorId"
                    component={Select}
                    options={guarantors}
                    fluid
                />
              </StyledGuarantorContainer>
              {!values.guarantorId &&
              <EmptyDataMessage messageKey="collectiveBill.pleaseSelectGuarantor"/>
              }
              <PxGrid>
                {values.guarantorId &&
                <Grid.Row>
                  <Grid.Column width={10}>
                    <CollectiveBillTreatmentsTable
                        selected={values.selected}
                        onChangeSelected={this.updateSelectedTreatment(form)}
                    />
                  </Grid.Column>
                  <Grid.Column width={6}>
                    <CollectiveBillInfoSegment
                        collectiveBillPriceInfo={values.collectiveBillPriceInfo}
                    />
                  </Grid.Column>
                </Grid.Row>
                }
              </PxGrid>
            </FormContent>
            <FormBottomContent>
              <CollectiveBillAdditionalInfo
                  mandateBanks={mandateBanks}
              />
            </FormBottomContent>
            <FormButtonsRow>
              <Button
                  compact
                  type="submit"
                  secondary
                  disabled={disabledButtons}
                  onClick={() => form.change("proofList", true)}
              >
                {getMessage("collectiveBill.printProoflist")}
              </Button>
              <Button
                  compact
                  type="submit"
                  primary
                  disabled={disabledButtons}
                  onClick={() => form.change("proofList", false)}
              >
                {getMessage("bill")}
              </Button>
            </FormButtonsRow>
          </StyledFormContainer>
        </StyledForm>
    );
  };

}

export default withAuthContext(withMessages(CollectiveBillView));
